import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    CircularProgress,
    Avatar,
    AvatarGroup,
    Chip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import PaymentIcon from '@mui/icons-material/Payment';
import dayjs from 'dayjs';
import { API_BASE_URL } from 'config/api';

const bookingTypeLabels = {
    'HD': 'Hot Desk',
    'DS': 'Desk',
    'MR': 'Meeting Room',
    'TB': 'Table',
    'ST': 'Suite',
    'OS': 'Open Space',
};

const JoinBookingDialog = ({
    open,
    onClose,
    booking,
    onConfirm,
    loading = false,
}) => {
    if (!booking) return null;

    const price = parseFloat(booking.price || 0);
    const vatAmount = parseFloat(booking.vat_amount || 0);
    const totalPrice = price + vatAmount;
    // Processing fee is 0 for community bookings
    const grandTotal = totalPrice;

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return dayjs(`2000-01-01 ${timeString}`).format('h:mm A');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                    Join Community Booking
                </Typography>
            </DialogTitle>
            
            <DialogContent>
                {/* Booking Details */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                        {booking.venue?.name || 'Venue Booking'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <CalendarTodayIcon fontSize="small" />
                            <Typography variant="body2">
                                {dayjs(booking.planned_start_day).format('dddd, MMMM D, YYYY')}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="body2">
                                {formatTime(booking.planned_start_time)} - {formatTime(booking.planned_end_time)}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                            <PlaceIcon fontSize="small" />
                            <Typography variant="body2">
                                {bookingTypeLabels[booking.booking_type] || 'Space'}
                            </Typography>
                        </Box>
                        
                        {booking.participant_count > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                <PeopleIcon fontSize="small" />
                                <Typography variant="body2">
                                    {booking.participant_count} {booking.participant_count === 1 ? 'person' : 'people'} already joined
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Participants Preview */}
                {booking.participants && booking.participants.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Current participants:
                        </Typography>
                        <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
                            {booking.participants.map((participant, idx) => (
                                <Avatar
                                    key={idx}
                                    src={participant.avatar ? `${API_BASE_URL}${participant.avatar}` : undefined}
                                    alt={participant.name}
                                    sx={{ width: 32, height: 32 }}
                                >
                                    {participant.name?.charAt(0)}
                                </Avatar>
                            ))}
                        </AvatarGroup>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Price Breakdown */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                        Payment Details
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                Space booking
                            </Typography>
                            <Typography variant="body2">
                                £{price.toFixed(2)}
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                VAT (20%)
                            </Typography>
                            <Typography variant="body2">
                                £{vatAmount.toFixed(2)}
                            </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" fontWeight={600}>
                                Total
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="primary">
                                £{grandTotal.toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Available Slots Info */}
                {booking.available_slots !== undefined && booking.available_slots !== 99 && (
                    <Chip
                        label={`${booking.available_slots} ${booking.available_slots === 1 ? 'spot' : 'spots'} left`}
                        size="small"
                        color={booking.available_slots <= 2 ? 'warning' : 'default'}
                        sx={{ mb: 2 }}
                    />
                )}
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button 
                    onClick={onClose} 
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm(booking)}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <PaymentIcon />}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#FFC107',
                        color: '#000',
                        '&:hover': {
                            backgroundColor: '#FFB300',
                        },
                    }}
                >
                    {loading ? 'Processing...' : `Pay £${grandTotal.toFixed(2)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JoinBookingDialog;
